import { useCallback, useMemo } from 'react';
import { useMutation, InfiniteData, useInfiniteQuery } from 'react-query';
import message from 'antd/es/message';
import produce from 'immer';

import { request, toastError, useCurrentUserId } from '@/utils';
import type { FeedArticleDetail } from '@/views/Stream/types';
import { FEEDS_PAGE_COUNT, setQueryAllFeedDetailByArticleId } from '@/views/Stream/hooks';
import { transArticle, transComment } from '@/views/Stream/utils';

import { CHANNEL_STREAM_LIST_PREFIX, invalidateChannelStreamList } from './query';

const STREAM_TYPES = {
  main: {
    url: '/channel/articles',
    order_bys: '-is_pinned,-pinned_at,-realized_value',
  },
  recent: {
    url: '/channel/articles',
    order_bys: '-create_at',
  },
};

export type ChannelStreamType = keyof typeof STREAM_TYPES;

export const useChannelStreamList = (id: string, type: ChannelStreamType) => {
  const currentId = useCurrentUserId();

  const { url, order_bys } = STREAM_TYPES[type];

  const select = useCallback((data: InfiniteData<Record<'articles', FeedArticleDetail[]>>) => {
    return produce(data, draft => {
      draft.pages.forEach(page =>
        page.articles?.forEach(feed => {
          transArticle(feed.article);
          feed.comments?.forEach(x => transComment(x));
        }),
      );
    });
  }, []);

  const queryKey = useMemo(
    () => [CHANNEL_STREAM_LIST_PREFIX, currentId, id, type],
    [currentId, type, id],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    // 没有使用 address 做为 key，考虑到后面的 setQueryData 可能不知道这个 address；
    // 而且同一时间，也不应该出现相同 type/url，不同 address 的 list；
    // 用 url 和 currentId 就足够了
    queryKey,
    async ({ pageParam }) => {
      const params: any = {
        id,
        start: 0,
        limit: FEEDS_PAGE_COUNT,
        order_bys,
      };

      if (pageParam) {
        Object.assign(params, pageParam);
      }

      return request.get<Record<'articles', FeedArticleDetail[]>>(url, params);
    },
    {
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const lastPageSize = lastPage.articles?.length;
        if (lastPageSize >= FEEDS_PAGE_COUNT) {
          return {
            start: lastPageSize + (pages.length - 1) * FEEDS_PAGE_COUNT,
          };
        }
      },
      select,
      onError(err: any) {
        toastError(err);
      },
    },
  );

  const result = useMemo(
    () => data?.pages?.flatMap(x => x.articles?.filter(x => !x.article.is_withdrawn)),
    [data],
  );

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};

export const useChannelStreamWithdraw = (article_id: number, channel_id?: string) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    () => {
      return request.post<{ is_success: boolean }>('/channel/article/withdraw', {
        article_id,
        id: channel_id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          message.error('Failed');

          return;
        }
        message.success('Hidden');

        if (!currentAddress) {
          return;
        }

        const recipe = (pre?: FeedArticleDetail) => {
          if (pre) {
            pre.article.is_withdrawn = true;
          }
        };

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId: article_id,
          recipe,
          includeQuote: true,
        });
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

export const useChannelStreamPin = (article_id: number, isPinned: boolean, channel_id?: string) => {
  const currentId = useCurrentUserId();

  return useMutation(
    () => {
      return request.post<{ is_success: boolean }>('/channel/article/update_is_pinned', {
        id: channel_id,
        article_id,
        is_pinned: !isPinned,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          toastError(res);
          return;
        } else {
          message.success(isPinned ? 'Unpinned' : 'Pinned');
        }
        if (!currentId) {
          return;
        }

        invalidateChannelStreamList(currentId, channel_id!, 'main');
      },
      onError(err: any) {
        toastError(err);
      },
    },
  );
};
